/**
 * Utilities for handling HTTP requests and error formatting
 */

import i18n from 'i18next';

export interface FormattedError {
  /** Mensaje de error legible para el usuario */
  message: string;
  /** Código de estado HTTP */
  statusCode?: number;
  /** Si debería hacer retry */
  shouldRetry: boolean;
  /** Respuesta original de la API para acceso a datos adicionales */
  response?: any;
  /** Errores de validación por campo (Laravel `errors` o dominio con `field`) */
  fieldErrors: Record<string, string[]>;
  /** Campo de dominio cuando el backend envía `error.field` */
  field: string | null;
}

export interface ExtractedApiError {
  summaryMessage: string | null;
  fieldErrors: Record<string, string[]>;
  field: string | null;
}

/**
 * Normaliza el body de error de API: Laravel `{ message, errors }` o dominio
 * `{ error: { message, field } }`. No prioriza el resumen sobre los campos.
 */
export const extractApiErrorDetails = (data: unknown): ExtractedApiError => {
  if (!data || typeof data !== 'object') {
    return { summaryMessage: typeof data === 'string' ? data : null, fieldErrors: {}, field: null };
  }

  const body = data as {
    message?: unknown;
    errors?: unknown;
    error?: unknown;
    data?: unknown;
  };

  const fieldErrors = normalizeFieldErrors(body.errors);
  let field: string | null = null;
  let summaryMessage: string | null = null;

  if (body.error && typeof body.error === 'object') {
    const domain = body.error as { message?: unknown; field?: unknown; error_description?: unknown };
    const domainMessage =
      typeof domain.message === 'string'
        ? domain.message
        : typeof domain.error_description === 'string'
          ? domain.error_description
          : null;
    if (typeof domain.field === 'string' && domain.field.length > 0 && domainMessage) {
      field = domain.field;
      if (Object.keys(fieldErrors).length === 0) {
        fieldErrors[domain.field] = [domainMessage];
      }
    }
    if (domainMessage) {
      summaryMessage = domainMessage;
    }
  } else if (typeof body.error === 'string') {
    summaryMessage = body.error;
  }

  if (!summaryMessage && typeof body.message === 'string') {
    summaryMessage = body.message;
  }
  if (!summaryMessage && typeof body.data === 'string') {
    summaryMessage = body.data;
  }
  if (!summaryMessage && typeof body.errors === 'string') {
    summaryMessage = body.errors;
  }

  return { summaryMessage, fieldErrors, field };
};

export const normalizeFieldErrors = (errors: unknown): Record<string, string[]> => {
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) {
    return {};
  }

  const result: Record<string, string[]> = {};
  for (const [key, value] of Object.entries(errors as Record<string, unknown>)) {
    if (Array.isArray(value)) {
      result[key] = value.map((item) => String(item));
    } else if (typeof value === 'string') {
      result[key] = [value];
    }
  }
  return result;
};

export const joinFieldErrorMessages = (fieldErrors: Record<string, string[]>): string =>
  Object.values(fieldErrors)
    .flat()
    .map((message) => message.trim())
    .filter(Boolean)
    .join('\n');

/**
 * Formats error messages from API responses
 * @param error - The error object from axios or other HTTP client
 * @returns Formatted error object with essential information
 */
export const formatError = (error: any): FormattedError => {
  const statusCode = error.response?.status;
  const responseBody = error.response?.data;

  // Determinar si debería hacer retry
  const rawMessage = typeof error.message === 'string' ? error.message : '';
  const isNetworkError =
    !error.response &&
    (error.code === 'NETWORK_ERROR' ||
      error.code === 'ERR_NETWORK' ||
      error.code === 'ECONNABORTED' ||
      /network error/i.test(rawMessage) ||
      /failed to fetch/i.test(rawMessage));
  const isServerError = statusCode >= 500 && statusCode < 600;

  // Solo retry en errores de servidor o red, no en errores de cliente
  const shouldRetry = isServerError || isNetworkError;

  const extracted = extractApiErrorDetails(responseBody);

  // Extraer mensaje de error
  let message = 'Error inesperado';
  let fieldErrors: Record<string, string[]> = {};
  let field: string | null = null;

  // Red / sin respuesta: no mostrar el inglés crudo de axios («Network Error»)
  if (isNetworkError) {
    message = i18n.t('errors.network_error', {
      ns: 'ui',
      defaultValue: 'Error de conexión.',
    });
  } else if (isServerError) {
    // Un 5xx nunca muestra el texto crudo del backend (puede filtrar trazas internas)
    message = i18n.t('errors.server_error', {
      ns: 'ui',
      defaultValue: 'Error del servidor. Inténtalo de nuevo más tarde.',
    });
  } else {
    fieldErrors = extracted.fieldErrors;
    field = extracted.field;

    if (extracted.summaryMessage) {
      message = extracted.summaryMessage;
    } else if (error.message) {
      message = error.message;
    }
  }

  return {
    message,
    statusCode,
    shouldRetry,
    response: error.response,
    fieldErrors,
    field,
  };
};

/**
 * Legacy function for backward compatibility
 * @deprecated Use formatError instead for better error handling
 */
export const formatErrorLegacy = (error: any): string => {
  return formatError(error).message;
};

/**
 * Extracts page parameter from URL
 * @param urlP - The URL string to parse
 * @returns The page parameter value or null
 */
export const getPageParam = (urlP: string): string | null => {
  if (!urlP) return null;
  const url = new URL(urlP);
  const params = new URLSearchParams(url.search);
  const page = params.get('page');
  return page;
};
